import React, { useEffect, useState } from 'react';
import { fetchTriageStats } from '../lib/api';
import { TriageStats } from '../types';
import { BarChart2, Brain, EyeOff, Calculator, FileQuestion } from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<TriageStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchTriageStats();
        setStats(data);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center bg-paper-50 dark:bg-study-slate">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-400 border-t-zinc-800 dark:border-zinc-600 dark:border-t-zinc-200" />
      </div>
    );
  }

  const failureLabels = {
    OBSCURED_THEORY: {
      label: 'Falha de Decodificação',
      desc: 'Não percebeu o assunto ou modelo',
      icon: EyeOff,
      color: 'bg-rose-500',
    },
    FORGOT_FORMULA: {
      label: 'Falha de Memória',
      desc: 'Sabia o assunto, esqueceu a fórmula',
      icon: Brain,
      color: 'bg-study-amber-500',
    },
    CALCULATION_ERROR: {
      label: 'Falha de Execução',
      desc: 'Montou certo, travou nos dados/cálculo',
      icon: Calculator,
      color: 'bg-blue-500',
    },
    MISREAD_INPUTS: {
      label: 'Leitura Incorreta de Dados',
      desc: 'Deixou passar grandezas ou unidades',
      icon: FileQuestion,
      color: 'bg-study-teal-500',
    },
  };

  const totalFailures = stats
    ? Object.values(stats.failureBreakdown).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-paper-50 px-4 py-8 dark:bg-study-slate sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-study-teal-600 dark:text-study-teal-400" />
            <h1 className="font-serif text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Dashboard de Falhas por Causa-Raiz
            </h1>
          </div>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
            Diagnóstico taxonômico para eliminação cirúrgica de atritos no IFF Campos Centro.
          </p>
        </div>

        {/* Summary Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="rounded-xl border border-paper-300 bg-paper-100 p-4 dark:border-study-line dark:bg-study-panel">
            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Total de Triagens
            </p>
            <p className="mt-1 font-mono text-2xl font-bold text-zinc-900 dark:text-zinc-100">
              {stats?.totalAttempts || 0}
            </p>
          </div>

          <div className="rounded-xl border border-paper-300 bg-paper-100 p-4 dark:border-study-line dark:bg-study-panel">
            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Tempo Médio
            </p>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-mono text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {stats?.avgTimeSeconds || 0}
              </span>
              <span className="font-mono text-xs text-zinc-500">segundos</span>
            </div>
          </div>

          <div className="rounded-xl border border-paper-300 bg-paper-100 p-4 dark:border-study-line dark:bg-study-panel">
            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Precisão de Reconhecimento
            </p>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-mono text-2xl font-bold text-study-teal-700 dark:text-study-teal-400">
                {stats?.accuracyRate || 0}%
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-paper-300 bg-paper-100 p-4 dark:border-study-line dark:bg-study-panel">
            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Foco do Edital
            </p>
            <p className="mt-1 text-xs font-semibold text-study-teal-700 dark:text-study-teal-400">
              Matemática (x5) + Natureza (x2)
            </p>
          </div>
        </div>

        {/* Root-Cause Failure Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="rounded-lg border border-paper-300 bg-paper-100 p-5 dark:border-study-line dark:bg-study-panel">
            <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100 border-b border-paper-200 pb-2 dark:border-study-line">
              Distribuição de Causas de Travamento
            </h3>

            <div className="mt-4 space-y-4">
              {(Object.keys(failureLabels) as (keyof typeof failureLabels)[]).map((key) => {
                const count = stats?.failureBreakdown[key] || 0;
                const pct = totalFailures > 0 ? Math.round((count / totalFailures) * 100) : 0;
                const info = failureLabels[key];
                const Icon = info.icon;

                return (
                  <div key={key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-1.5 font-semibold text-zinc-800 dark:text-zinc-200">
                        <Icon className="h-3.5 w-3.5 text-zinc-500" />
                        <span>{info.label}</span>
                      </div>
                      <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
                        {count} ({pct}%)
                      </span>
                    </div>

                    <div className="h-2 w-full rounded-full bg-paper-200 dark:bg-study-card overflow-hidden">
                      <div
                        className={`h-full ${info.color} transition-all duration-300`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                      {info.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Subject Distribution */}
          <div className="rounded-lg border border-paper-300 bg-paper-100 p-5 dark:border-study-line dark:bg-study-panel">
            <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100 border-b border-paper-200 pb-2 dark:border-study-line">
              Volume por Componente Curricular
            </h3>

            <div className="mt-4 space-y-3">
              {stats && Object.keys(stats.subjectCounts).length > 0 ? (
                Object.entries(stats.subjectCounts).map(([subject, count]) => {
                  const isWeight5 = subject === 'MATEMATICA';
                  const isWeight2 = ['FISICA', 'QUIMICA', 'BIOLOGIA'].includes(subject);

                  return (
                    <div
                      key={subject}
                      className="flex items-center justify-between rounded border border-paper-200 bg-paper-50 p-2.5 dark:border-study-line dark:bg-study-card text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-zinc-800 dark:text-zinc-200">
                          {subject}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-paper-200 text-zinc-600 dark:bg-study-panel dark:text-zinc-400">
                          {isWeight5 ? 'Peso 5' : isWeight2 ? 'Peso 2' : 'Peso 1'}
                        </span>
                      </div>
                      <span className="font-mono font-bold text-zinc-700 dark:text-zinc-300">
                        {count} triagem(ns)
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center py-6">
                  Nenhuma triagem registrada ainda. Inicie um ciclo na aba Triagem.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
